import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureType } from '@core/enums/feature-type.enum';
import { ACCESS } from '@core/constant/access';
import { ImageColorType } from '@core/enums/image-color-type.enum';
import { NavigationTile } from '@core/interfaces/navigation-tile.interface';
import { FeatureImages } from '@core/utils/feature-images';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@services/config/config.service';
import { CreateCastMemberModalComponent } from '@app/shared/modals/admin/create-cast-member-modal/create-cast-member-modal.component';
import { CreateCharacterModalComponent } from '@app/shared/modals/admin/create-character-modal/create-character-modal.component';
import { AuthService } from '@app/core/services/auth/auth.service';
import { Subscription } from 'rxjs';
import { State } from '@app/shared/store/reducers';
import { select, Store } from '@ngrx/store';
import { getCurrUser } from '@app/shared/store/selectors/user-claims.selector';
import * as _ from 'lodash';

@Component({
  standalone: false,
  selector: 'app-navigation-tiles',
  templateUrl: './navigation-tiles.component.html',
  styleUrls: ['./navigation-tiles.component.scss'],
})

export class NavigationTilesComponent implements OnInit, OnDestroy {
  @ViewChild(CreateCastMemberModalComponent) createCastModal: CreateCastMemberModalComponent;
  @ViewChild(CreateCharacterModalComponent) createCharModal: CreateCharacterModalComponent;
  @Input() public tileTypes: FeatureType[];
  public tiles: NavigationTile[] = [];
  public translations: any;
  public userState$: Subscription;
  public translationService$: Subscription;

  constructor(
    private store: Store<State>,
    private configService: ConfigService,
    private router: Router,
    private authService: AuthService,
    private translateService: TranslateService) {
  }

  public getTranslations() {
    this.translationService$ = this.translateService.get([
      'Navigation.Admin',
      'Navigation.CastMeasurements',
      'Navigation.CharacterMeasurements',
      'Navigation.GarmentUtilizationService',
      'Navigation.NameTags',
      'Navigation.Shoe',
      'Navigation.CreateCast',
      'Navigation.CreateCharacter'
    ]).subscribe((translations) => {
      this.translations = translations;
    });
  }

  public ngOnInit() {
    this.getTranslations();
    this.userState$ = this.store.pipe(select(getCurrUser)).subscribe((res: any) => {
      if (res) {
        this.updateNavigationTiles(this.tileTypes);
      }
    });
  }

  public updateNavigationTiles(tileTypes: any[]) {
    if (!tileTypes) {
      return;
    }
    this.tiles = tileTypes.map((type) => this.navigationTileFromType(type));
    this.tiles = this.tiles.filter(item => {
      const hasAnyPermission = this.authService.hasAnyAbility(item.requiredAbilities);

      return hasAnyPermission || _.isEmpty(item.requiredAbilities);
    });
  }

  public onTileClick(tile: NavigationTile) {
    console.log('clicked tile ' + JSON.stringify(tile));

    /* istanbul ignore else */
    if (tile.route != '') {
      this.router.navigate([tile.route]);
    }

    if (tile.url != '') {
      const url = [tile.url].toString();
      window.open(url);
    }

    if (tile.type === FeatureType.CreateCast) {
      this.createCastModal.open();
    }

    if (tile.type === FeatureType.CreateCharacter) {
      this.createCharModal.open(false);
    }
  }

  private navigationTileFromType(type: FeatureType): NavigationTile {
    switch (type) {
      case FeatureType.Admin:
        return {
          type: FeatureType.Admin,
          title: this.translations['Navigation.Admin'],
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.Admin }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.Admin, colorType: ImageColorType.Inverted }),
          route: '/admin',
          requiredAbilities: ACCESS.CTA.ADMIN,
          url: ''
        };
      case FeatureType.CastMeasurements:
        return {
          type: FeatureType.CreateCast,
          title: this.translations['Navigation.CreateCast'],
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.CreateCast }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.CreateCast, colorType: ImageColorType.Inverted }),
          route: '',
          requiredAbilities: ACCESS.CTA.CAST_EDITOR,
          url: ''
        };
      case FeatureType.CreateCharacter:
        return {
          type: FeatureType.CreateCharacter,
          title: this.translations['Navigation.CreateCharacter'],
          color: FeatureImages.getImageBackgroundColor({ featureType: FeatureType.CreateCharacter }),
          image: FeatureImages.getImagePath({ featureType: FeatureType.CreateCharacter, colorType: ImageColorType.Inverted }),
          route: '',
          requiredAbilities: ACCESS.CTA.CHARACTER_EDITOR,
          url: ''
        };
    }
  }

  ngOnDestroy(): void {
    this.userState$.unsubscribe();
    this.translationService$.unsubscribe();
  }
}
